import { createLogger } from '@/lib/logger';

const logger = createLogger('store/factories/createUIStore');

/**
 * Generic UI store factory for Zustand
 * Creates standardized UI state stores for any module
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { UIStoreState, UIStoreConfig } from '@/types/store.types';
import { createSelectors } from './createSelectors';
/**
 * Creates a UI state store for a module
 * 
 * @example
 * ```ts
 * export const useProjectUIStore = createUIStore({
 *   storageKey: 'project-ui-storage',
 *   defaultViewMode: 'list',
 *   availableViewModes: ['list', 'board', 'calendar'],
 *   persistSelections: false,
 * });
* ```
 */
type PersistedSet = { __type: 'Set'; value: string[] };

function isPersistedSet(value: unknown): value is PersistedSet {
  return Boolean(
    value &&
    typeof value === 'object' &&
    (value as PersistedSet).__type === 'Set' &&
    Array.isArray((value as PersistedSet).value)
  );
}

export function createUIStore(config: UIStoreConfig) {
  const {
    storageKey,
    defaultViewMode,
    availableViewModes = [],
    persistSelections = false,
  } = config;

  const storage = createJSONStorage<UIStoreState>(() => localStorage, {
    replacer: (_key, value) => {
      if (value instanceof Set) {
        return { __type: 'Set', value: Array.from(value) } satisfies PersistedSet;
      }
      return value;
    },
    reviver: (_key, value) => {
      if (isPersistedSet(value)) {
        return new Set(value.value);
      }
      return value;
    },
  });

  const store = create<UIStoreState>()(
    persist(
      immer<UIStoreState>((set) => ({
        viewMode: defaultViewMode,
        selectedIds: new Set<string>(),
        collapsedGroups: new Set<string>(),
        sidebarOpen: false,
        filterPanelOpen: false,

        setViewMode: (mode: string) => {
          if (availableViewModes.length > 0 && !availableViewModes.includes(mode)) {
            logger.warn(
              `[UIStore] Invalid view mode "${mode}". Available modes: ${availableViewModes.join(', ')}`
            );
            return;
          }

          set((state) => {
            state.viewMode = mode;
          });
        },

        toggleSelection: (id: string) =>
          set((state) => {
            if (state.selectedIds.has(id)) {
              state.selectedIds.delete(id);
            } else {
              state.selectedIds.add(id);
            }
          }),

        selectAll: (ids: string[]) =>
          set((state) => {
            state.selectedIds = new Set(ids);
          }),

        clearSelection: () =>
          set((state) => {
            state.selectedIds = new Set();
          }),

        toggleGroup: (groupId: string) =>
          set((state) => {
            if (state.collapsedGroups.has(groupId)) {
              state.collapsedGroups.delete(groupId);
            } else {
              state.collapsedGroups.add(groupId);
            }
          }),

        toggleSidebar: () =>
          set((state) => {
            state.sidebarOpen = !state.sidebarOpen;
          }),

        toggleFilterPanel: () =>
          set((state) => {
            state.filterPanelOpen = !state.filterPanelOpen;
          }),

        reset: () =>
          set((state) => {
            state.viewMode = defaultViewMode;
            state.selectedIds = new Set();
            state.collapsedGroups = new Set();
            state.sidebarOpen = false;
            state.filterPanelOpen = false;
          }),
      })),
      {
        name: storageKey,
        storage,
        partialize: (state) => ({
          viewMode: state.viewMode,
          collapsedGroups: state.collapsedGroups,
          ...(persistSelections ? { selectedIds: state.selectedIds } : {}),
        }),
      }
    )
  );

  return createSelectors(store);
}

